// Files on the Admin page (RFC-054 §6, byj-1wv.70.6, byj-1wv.71.4): what
// file storage costs — everyone's files, the total they may take, what the
// quotas given could reach, this month's downloads — at the prices the admin
// sets with the default quota and the total; then each person: what theirs
// take and cost, their quota and the most it costs, their downloads; a
// quota given by address before someone's first file; their files, and one
// removed. Prices say what storage costs; nobody is charged. Laid out in
// /shares' way (byj-1wv.51.13): the figures as tiles; the settings as rows
// under them, each edited alone from its row; Add a person beside the tab's
// lead; the people as rows, Change quota and View files — their files in a
// drawer — behind a row's ⋯, Select on the heading to change several.
import { useCallback, useEffect, useState } from "react";
import { isAddress } from "../address";
import { api, type AdminUser, type FileLimits, type FileSettings, type FileUse, type ShareFile } from "../api";
import { t, whenMinute, phrases } from "../i18n";
import { Choices } from "./Choices";
import { useConfirm } from "./Confirm";
import { Drawer } from "./Drawer";
import { FormDialog } from "./FormDialog";
import { FileRow } from "./FileRow";
import { Meter } from "./Meter";
import { PersonField } from "./PersonField";
import { RowMenu, type MenuItem } from "./RowMenu";
import { navigate } from "../router";
import { EmptyRows, Grouped, initialOf } from "./Rows";
import { alike, eachPicked, SelectBox, SelectControls, useSelectMode } from "./Selection";
import { fileBytes } from "../files";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The quotas an admin picks from, in MB, beside the default and a number of their own. */
export const fileQuotaChoices = [100, 250, 500, 1024, 2048, 5120, 10240];

const gb = 1 << 30;

/** What n bytes cost at usdPerGB, a GB being 2³⁰ bytes as S3 bills it. */
export const costOf = (n: number, usdPerGB: number) => (n / gb) * usdPerGB;

/** A cost as a person reads it: "$0", "under $0.01", "$0.25", "$120". */
export function usd(x: number): string {
  if (x <= 0) return "$0";
  if (x < 0.01) return t("admin:storageUnderCent");
  return x < 100 ? `$${x.toFixed(2)}` : `$${Math.round(x)}`;
}

/** The most something may cost, up to the next cent: "$0.01" for a fraction of one. */
export function usdUp(x: number): string {
  if (x <= 0) return "$0";
  return x < 100 ? `$${(Math.ceil(x * 100 - 1e-9) / 100).toFixed(2)}` : `$${Math.ceil(x)}`;
}

/** A price as set: "$0.025", "$0.126", "$1". */
export const usdPrice = (x: number) => `$${Number(x.toFixed(4))}`;

/** The calls the section makes, so a test can supply its own. */
export type FileStorageApi = Pick<typeof api, "adminFiles" | "adminFilesOf" | "setFileQuota" | "adminRemoveFile" | "fileSettings" | "setFileSettings" | "adminUsers">;

type Usage = Awaited<ReturnType<typeof api.adminFiles>>;

type Key = "quota" | "total" | "month" | "out";

/**
 * A setting as its dialog holds it: the value that holds, as typed, and
 * whether it is an admin's own — the relay's flag holds otherwise, and is
 * what an own value is put back to. An emptied field is the relay's too.
 */
type Field = { value: string; own: boolean };

const limitOf: Record<Key, keyof FileLimits> = { quota: "default_quota", total: "total_limit", month: "usd_per_gb_month", out: "usd_per_gb_out" };

/** A setting as the form shows it: MB for a size, USD for a price. */
const shown = (k: Key, l: FileLimits) => String(k === "quota" || k === "total" ? l[limitOf[k]] / (1 << 20) : l[limitOf[k]]);

const fieldOf = (s: FileSettings, k: Key): Field => ({ value: shown(k, s.settings), own: s.set.includes(limitOf[k]) });

/**
 * What the relay is sent, all four at once as it takes them: `k` as its
 * dialog holds it, the others as they stand; an own value as a number, the
 * relay's as null.
 */
function settingsOut(s: FileSettings, k: Key, f: Field) {
  const n = (x: Key) => {
    const v = x === k ? f : fieldOf(s, x);
    return v.own && v.value.trim() !== "" ? Number(v.value) : null;
  };
  return { default_quota_mb: n("quota"), total_limit_mb: n("total"), usd_per_gb_month: n("month"), usd_per_gb_out: n("out") };
}

/**
 * A setting as the page names it: what it is, what it does, and its unit —
 * "$" before a price's value, a size's or a price's measure after it.
 */
const settingOf: Record<Key, { label: () => string; hint: () => string; unit: () => string; step: string }> = {
  quota: { label: () => t("admin:storageDefaultQuota"), hint: () => t("admin:storageQuotaHint"), unit: () => "MB", step: "1" },
  total: { label: () => t("admin:storageTotal"), hint: () => t("admin:storageTotalHint"), unit: () => "MB", step: "1" },
  month: { label: () => t("admin:storagePriceMonth"), hint: () => t("admin:storagePriceMonthHint"), unit: () => t("admin:storagePerGBMonthUnit"), step: "0.001" },
  out: { label: () => t("admin:storagePriceOut"), hint: () => t("admin:storagePriceOutHint"), unit: () => t("admin:storagePerGBUnit"), step: "0.001" },
};

/** A setting as a person reads it: "100.0 MB", "no limit", "$0.025 per GB a month". */
function said(k: Key, l: FileLimits): string {
  if (k === "quota") return fileBytes(l.default_quota);
  if (k === "total") return l.total_limit > 0 ? fileBytes(l.total_limit) : t("admin:storageNoTotal");
  return `${usdPrice(l[limitOf[k]])} ${settingOf[k].unit()}`;
}

/** The settings, in the order the page lists them. */
const settingKeys: Key[] = ["quota", "total", "month", "out"];

/**
 * One setting, a short form over the page (components/FormDialog) opened
 * by the Edit on its row: one of two — as Share is deployed, its value said,
 * or a value of the admin's own, typed in its field — picked with the
 * shared Choices, as the bank's settings are (byj-1wv.93). The dialog picks; the page
 * saves, the others sent as they stand.
 */
function SettingsDialog({ s, k, onSubmit, onClose }: {
  s: FileSettings;
  k: Key;
  onSubmit: (f: Field) => Promise<void>;
  onClose: () => void;
}) {
  const [f, setF] = useState<Field>(() => fieldOf(s, k));
  const dirty = JSON.stringify(settingsOut(s, k, f)) !== JSON.stringify(settingsOut(s, k, fieldOf(s, k)));
  const { label, hint, unit, step } = settingOf[k];
  const own = f.own;
  return (
    <FormDialog
      id="settings-dialog"
      testid="file-settings"
      title={label()}
      submitLabel={t("common:save")}
      canSubmit={dirty}
      onSubmit={() => onSubmit(f)}
      onClose={onClose}
    >
      <Choices<"deployed" | "own">
        name={`storage-${k}`}
        legend={<span className="muted">{hint()}</span>}
        value={own ? "own" : "deployed"}
        onChange={(v) => setF(v === "own" ? { ...f, own: true } : { value: shown(k, s.relay), own: false })}
        testid={`setting-${k}`}
        options={[
          // the value on the option's own line, bold with it as every choice's name is, never under it in muted words
          { value: "deployed", name: `${t("admin:storageAsDeployed")} · ${said(k, s.relay)}` },
          {
            value: "own",
            name: t("admin:storageCustom"),
            extra: (
              <span className="num-field">
                {k === "month" || k === "out" ? <span className="muted small">$</span> : null}
                <input
                  type="number"
                  min="0"
                  step={step}
                  inputMode="decimal"
                  aria-label={label()}
                  value={f.value}
                  // typing a value of one's own picks Custom
                  onChange={(e) => setF({ value: e.target.value, own: true })}
                />
                <span className="muted small">{unit()}</span>
              </span>
            ),
          },
        ]}
      />
    </FormDialog>
  );
}

/**
 * The settings that make the figures, a group of rows under them as the
 * page's other lists are (Grouped): each what it is and what it does, its
 * value, whether it is as deployed or set here, and its Edit — a small text
 * button, as a share's Edit beside its name is (pages/Session), named by
 * what it edits so a voice saying "Edit" finds it; under the heading who
 * changed them last.
 */
function Settings({ s, open, onEdit }: { s: FileSettings; open: Key | null; onEdit: (k: Key) => void }) {
  return (
    <div data-testid="storage-settings">
      <Grouped<Key>
        id="settings"
        headed
        groups={[
          {
            title: t("admin:storageSettings"),
            items: settingKeys,
            uncounted: true,
            note: s.updated_by && <p className="muted small panel-lead">{t("admin:storageSetBy", { email: s.updated_by, when: whenMinute(s.updated_at ?? "") })}</p>,
          },
        ]}
        row={(k) => {
          const own = s.set.includes(limitOf[k]);
          return (
            <li key={k} className="share-row admin-row setting-row-item is-live" data-testid={`setting-row-${k}`}>
              <div className="row-main">
                <span className="row-title">{settingOf[k].label()}</span>
                <div className="row-sub">{settingOf[k].hint()}</div>
              </div>
              <div className="row-meta">
                <strong className="setting-value">{said(k, s.settings)}</strong>
                <span className={own ? "chip chip-ok" : "chip"}>{own ? t("admin:storageSetHere") : t("admin:storageAsDeployedChip")}</span>
                <button
                  type="button"
                  className="small"
                  aria-label={t("admin:storageEditSetting", { name: settingOf[k].label() })}
                  aria-haspopup="dialog"
                  aria-expanded={open === k}
                  aria-controls={open === k ? "settings-dialog" : undefined}
                  onClick={() => onEdit(k)}
                >
                  {t("common:edit")}
                </button>
              </div>
            </li>
          );
        }}
      />
    </div>
  );
}

/** What everyone's files take and cost, and what the quotas given could. */
function Totals({ usage, s }: { usage: Usage; s: FileSettings }) {
  const lim = s.settings;
  const stored = usage.people.reduce((n, p) => n + p.used, 0);
  const given = usage.people.reduce((n, p) => n + p.quota, 0);
  const share = lim.total_limit > 0 ? Math.min(1, stored / lim.total_limit) : 0;
  const full = lim.total_limit > 0 && stored >= lim.total_limit;
  return (
    <div className="storage-tiles" data-testid="file-totals">
      <div className="storage-tile">
        <span className="muted small">{t("admin:storageStored")}</span>
        <strong className={full ? "error" : ""}>
          {lim.total_limit > 0 ? t("admin:storageOfTotal", { used: fileBytes(stored), total: fileBytes(lim.total_limit) }) : fileBytes(stored)}
        </strong>
        {lim.total_limit > 0 && (
          <Meter label={t("admin:storageStored")} used={share} full={full} />
        )}
        <span className="muted small">{t("admin:storageMonthly", { cost: usd(costOf(stored, lim.usd_per_gb_month)) })}</span>
      </div>
      <div className="storage-tile">
        <span className="muted small">{t("admin:storageGiven")}</span>
        <strong>{fileBytes(given)}</strong>
        <span className="muted small">{t("admin:storageAtMost", { cost: usdUp(costOf(given, lim.usd_per_gb_month)) })}</span>
      </div>
      <div className="storage-tile">
        <span className="muted small">{t("admin:storageDownloads", { month: usage.month })}</span>
        <strong>{fileBytes(usage.downloaded)}</strong>
        <span className="muted small">
          {usd(costOf(usage.downloaded, lim.usd_per_gb_out))} · {t("admin:storageLastMonth", { bytes: fileBytes(usage.downloadedLast), cost: usd(costOf(usage.downloadedLast, lim.usd_per_gb_out)) })}
        </span>
      </div>
    </div>
  );
}

/** A person's quota as the dialog picks it: "default", or a number of MB. */
const quotaKey = (p: FileUse) => (p.quota_mb === undefined ? "default" : String(p.quota_mb));

/**
 * A person's quota, a short form over the page (components/FormDialog):
 * Add a person beside the tab's lead lists someone not listed yet, before
 * their first file, by a quota of their own (RFC-054: someone given a quota
 * is listed with no file) — the person found as they are typed, or an
 * address typed whole, which must be one who has signed in — or from a
 * person's row (`of`), said with what they use — or for several picked on
 * their rows (`many`), each given the same, starting from what they hold —
 * Mixed where they differ, each keeping its own. The quota: the default (put
 * back to it), a size with the most it costs a month, or a number of MB. The
 * dialog picks the quota and, adding, the person; the page sets it.
 */
function QuotaDialog({ lim, find, of, many, onSubmit, onClose }: {
  lim: FileLimits;
  find: (q: string) => Promise<AdminUser[]>;
  /** the person whose row it was opened from */
  of?: FileUse;
  /** the people picked on their rows */
  many?: FileUse[];
  /** the quota in MB, null for the default; adding, the person found */
  onSubmit: (mb: number | null, userId?: string) => Promise<void>;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  // the person picked from those found, so their address is not looked up again
  const [who, setWho] = useState<AdminUser | null>(null);
  // a person added is listed by a quota of their own (RFC-054: someone given
  // a quota is listed with no file), so the default is offered only to change
  // one listed, or several, back to it
  const adding = !of && many === undefined;
  // the default, a size offered, or a number of MB: as their quota stands,
  // the one's or what the several hold alike; Mixed where they differ
  const same = adding ? String(fileQuotaChoices[3]) : alike(of ? [of] : many!, quotaKey);
  const offered = (k: string) => k === "default" || fileQuotaChoices.includes(Number(k));
  const [choice, setChoice] = useState(same === null ? "mixed" : offered(same) ? same : "other");
  const [ownMb, setOwnMb] = useState(same !== null && !offered(same) ? same : "");
  const ownOk = /^\d+$/.test(ownMb.trim());
  const quotaOf = (c: string) => (c === "mixed" ? undefined : c === "default" ? null : Number(c === "other" ? ownMb.trim() : c));
  const quota = quotaOf(choice);
  // as it stands is nothing to save: a row's quota, or the several left Mixed
  const unchanged = !adding && quota === (same === null ? undefined : same === "default" ? null : Number(same));
  const price = (bytes: number) => t("admin:storageQuotaChoice", { bytes: fileBytes(bytes), cost: usdUp(costOf(bytes, lim.usd_per_gb_month)) });
  const submit = async () => {
    if (!adding) return onSubmit(quota!);
    // one added must have signed in: found by their whole address
    const want = email.trim().toLowerCase();
    const u = who?.email.toLowerCase() === want ? who : (await find(want)).find((x) => x.email.toLowerCase() === want);
    if (!u) throw new Error(t("admin:storageNobody", { email: want }));
    await onSubmit(quota!, u.id);
  };
  return (
    <FormDialog
      id="quota-dialog"
      testid="give-quota"
      title={many ? t("admin:peopleFilesQuotaForMany", { count: many.length }) : of ? t("admin:peopleFilesChangeQuotaFor", { email: of.email || of.user_id }) : t("admin:storageAddTitle")}
      // the row's said with what their files take, as a grant's with its month
      now={
        of
          ? t("admin:peopleFilesUsed", { used: fileBytes(of.used), quota: fileBytes(of.quota), count: of.files })
          : many && t("recordings:heldNow", { what: [...new Set(many.map((p) => (p.quota_mb === undefined ? t("admin:peopleFilesDefaultQuota", { quota: fileBytes(p.quota) }) : fileBytes(p.quota))))].join(", ") })
      }
      submitLabel={adding ? t("admin:storageAdd") : t("common:save")}
      canSubmit={(!adding || isAddress(email)) && choice !== "mixed" && (choice !== "other" || ownOk) && !unchanged}
      onSubmit={submit}
      onClose={onClose}
    >
      {/* the person is the row's or the ones picked: found only when adding */}
      {adding && (
        <PersonField
          find={find}
          label={t("admin:adminEmail")}
          ariaLabel={t("admin:storageGiveTo")}
          value={email}
          onChange={setEmail}
          onPick={(u) => {
            setWho(u);
            setEmail(u.email);
          }}
        />
      )}
      <label className="field">
        {t("admin:storageGiveQuota")}
        <select value={choice} onChange={(e) => setChoice(e.target.value)}>
          {same === null && <option value="mixed">{t("recordings:mixedKeeps")}</option>}
          {!adding && <option value="default">{t("admin:peopleFilesDefault", { quota: price(lim.default_quota) })}</option>}
          {fileQuotaChoices.map((n) => (
            <option key={n} value={String(n)}>
              {price(n * (1 << 20))}
            </option>
          ))}
          <option value="other">{t("admin:storageQuotaOther")}</option>
        </select>
      </label>
      {choice === "other" && (
        <label className="field">
          {t("admin:storageQuotaMB")}
          <span className="num-field">
            <input type="number" min="0" step="1" inputMode="numeric" aria-label={t("admin:storageQuotaMB")} value={ownMb} onChange={(e) => setOwnMb(e.target.value)} />
            <span className="muted small">MB</span>
          </span>
        </label>
      )}
    </FormDialog>
  );
}

/**
 * A person's row: what their files take of their quota and cost, their
 * downloads, "own quota" when one is set for them alone; behind ⋯ Change
 * quota and View files, their files in a drawer.
 */
function PersonFilesRow({ p, lim, select, onQuota, onShow }: {
  p: FileUse;
  lim: FileLimits;
  /** while several are being picked */
  select?: { picked: Set<string>; toggle: (id: string) => void };
  onQuota: (p: FileUse) => void;
  onShow: (p: FileUse) => void;
}) {
  const who = p.email || p.user_id;
  const over = p.used > p.quota;
  const menu: MenuItem[] = [
    { label: t("admin:peopleFilesChangeQuota"), onSelect: () => onQuota(p) },
    ...(p.files > 0 ? [{ label: t("admin:peopleFilesShow"), onSelect: () => onShow(p) }] : []),
  ];
  return (
    <li className="share-row admin-row is-live" data-testid="person-files">
      <span className="row-lead">{select && <SelectBox id={p.user_id} title={who} picked={select.picked} toggle={select.toggle} />}</span>
      <span className="kind-box" aria-hidden="true">
        {initialOf(who)}
      </span>
      <div className="row-main">
        <span className="row-title">{who}</span>
        <div className="row-sub">
          {p.name && <>{p.name} · </>}
          <span className={over ? "error" : undefined}>
            {t("admin:peopleFilesUsed", { used: fileBytes(p.used), quota: fileBytes(p.quota), count: p.files })} · {t("admin:storageMonthly", { cost: usd(costOf(p.used, lim.usd_per_gb_month)) })}
          </span>
          {(p.downloaded > 0 || p.downloaded_last > 0) && (
            <span>
              {" · "}
              {t("admin:storagePersonDownloads", { bytes: fileBytes(p.downloaded), cost: usd(costOf(p.downloaded, lim.usd_per_gb_out)) })}
              {p.downloaded_last > 0 && <> · {t("admin:storagePersonLastMonth", { bytes: fileBytes(p.downloaded_last) })}</>}
            </span>
          )}
        </div>
      </div>
      {/* the quota is in the line above; one set for them alone is marked */}
      <div className="row-meta">{p.quota_mb !== undefined && <span className="chip">{t("admin:peopleFilesOwnQuota")}</span>}</div>
      <RowMenu label={t("shares:moreFor", { title: who })} items={menu} />
    </li>
  );
}

export function FileStorage({ client = api, onCount }: { client?: FileStorageApi; onCount?: (n: number) => void }) {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [settings, setSettings] = useState<FileSettings | null>(null);
  // whose files the drawer shows, and them once loaded
  const [viewing, setViewing] = useState<FileUse | null>(null);
  const [theirs, setTheirs] = useState<ShareFile[] | null>(null);
  // the setting whose dialog is open, from the Edit on its row
  const [editing, setEditing] = useState<Key | null>(null);
  // the quota's dialog: from beside the lead, a person's row (`of`), or
  // several picked on their rows (`many`)
  const [giving, setGiving] = useState<{ of?: FileUse; many?: FileUse[] } | null>(null);
  const mode = useSelectMode();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const reload = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([client.adminFiles(), client.fileSettings()]);
      setUsage(u);
      setSettings(s);
      onCount?.(u.people.length);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, onCount]);
  useEffect(() => {
    void reload();
  }, [reload]);

  /** `f` done, said so or its refusal said */
  const act = async (f: () => Promise<unknown>, said?: string) => {
    try {
      await f();
      setError(null);
      setDone(said ?? null);
    } catch (err) {
      setError(errText(err));
      setDone(null);
    }
    await reload();
  };
  // saved, the dialog closes; refused, it stays open with the refusal in it
  const save = async (k: Key, f: Field) => {
    await client.setFileSettings(settingsOut(settings!, k, f));
    setEditing(null);
    setError(null);
    setDone(t("admin:storageSaved"));
    await reload();
  };
  // one person's set, the dialog closed; several, each set, what was done
  // said back and those refused left picked
  const give = async (g: { of?: FileUse; many?: FileUse[] }, mb: number | null, userId?: string) => {
    if (g.many) {
      const r = await eachPicked(g.many, (p) => client.setFileQuota(p.user_id, mb));
      setDone(phrases.bulkDone(r.done, r.refused));
      mode.keepOnly(r.left.map((p) => p.user_id));
    } else {
      await client.setFileQuota(g.of?.user_id ?? userId!, mb);
    }
    setGiving(null);
    await reload();
  };
  const show = async (p: FileUse) => {
    setViewing(p);
    setTheirs(null);
    try {
      setTheirs(await client.adminFilesOf(p.user_id));
    } catch (err) {
      setError(errText(err));
    }
  };
  const removeIt = async (p: FileUse, f: ShareFile) => {
    if (!(await ask(t("admin:peopleFileRemoveAsk", { name: f.name, email: p.email || p.user_id }), { action: t("admin:peopleFileRemove"), danger: true }))) return;
    await act(async () => {
      await client.adminRemoveFile(p.user_id, f.id);
      setTheirs((fs) => (fs ?? []).filter((x) => x.id !== f.id));
    }, t("admin:peopleFileRemoved", { name: f.name }));
  };

  const edit = (k: Key | null) => {
    setError(null);
    setDone(null);
    setEditing(k);
  };

  const lim = settings?.settings;
  return (
    <div className="stack" data-testid="file-storage">
      {/* the tab's one way to add, beside what the tab is: apart from what acts on the rows picked */}
      <div className="panel-head">
        <p className="muted small panel-lead">{t("admin:storageLead")}</p>
        <button
          className="primary"
          aria-haspopup="dialog"
          aria-expanded={giving !== null && !giving.of && !giving.many}
          aria-controls={giving && !giving.of && !giving.many ? "quota-dialog" : undefined}
          disabled={!lim}
          onClick={() => {
            setDone(null);
            setGiving({});
          }}
        >
          {t("admin:storageGiveOpen")}
        </button>
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}
      {usage === null || !settings || !lim ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <>
          <Totals usage={usage} s={settings} />
          <Settings s={settings} open={editing} onEdit={(k) => edit(k)} />
          <div>
            {/* the people, headed as Grouped heads every list on the page */}
            <Grouped
              id="people-files"
              headed
              groups={[
                {
                  title: t("admin:storagePeople"),
                  items: usage.people,
                  actions: (
                    <SelectControls
                      ids={usage.people.map((p) => p.user_id)}
                      mode={mode}
                      more={(n) => (
                        <button
                          className="small"
                          aria-haspopup="dialog"
                          onClick={() => {
                            setDone(null);
                            setGiving({ many: usage.people.filter((p) => mode.picked.has(p.user_id)) });
                          }}
                        >
                          {t("admin:peopleFilesChangeQuotas", { n })}
                        </button>
                      )}
                    />
                  ),
                  none: <EmptyRows text={t("admin:peopleFilesNone")} />,
                },
              ]}
              row={(p) => (
                <PersonFilesRow
                  key={p.user_id}
                  p={p}
                  lim={lim}
                  select={mode.selecting ? { picked: mode.picked, toggle: mode.toggle } : undefined}
                  onQuota={(x) => {
                    setDone(null);
                    setGiving({ of: x });
                  }}
                  onShow={(x) => void show(x)}
                />
              )}
            />
            {editing && <SettingsDialog s={settings} k={editing} onSubmit={(f) => save(editing, f)} onClose={() => edit(null)} />}
            {giving && (
              <QuotaDialog
                lim={lim}
                find={client.adminUsers}
                of={giving.of}
                many={giving.many}
                onSubmit={(mb, userId) => give(giving, mb, userId)}
                onClose={() => setGiving(null)}
              />
            )}
          </div>
        </>
      )}
      {/* a person's files, browsed beside the page: what they take of their
          quota, then each as the page's rows are, Open and Remove behind ⋯ */}
      <Drawer id="their-files-drawer" title={viewing ? t("admin:peopleFilesOf", { email: viewing.email || viewing.user_id }) : ""} open={viewing !== null} onClose={() => setViewing(null)}>
        {viewing && (
          <p className="muted small" data-testid="their-use">
            {t("admin:peopleFilesUsed", { used: fileBytes(viewing.used), quota: fileBytes(viewing.quota), count: viewing.files })}
          </p>
        )}
        {theirs === null ? (
          <p className="muted">{t("common:loading")}</p>
        ) : theirs.length === 0 ? (
          <EmptyRows text={t("admin:peopleFilesNoneLeft")} />
        ) : (
          <div className="rows-panel">
            <ul className="rows" data-testid="their-files">
              {theirs.map((f) => (
                <FileRow
                  key={f.id}
                  f={f}
                  // every row is theirs, said in the drawer's title
                  owner={false}
                  menu={[
                    { label: t("files:openFile"), onSelect: () => navigate(`/f/${encodeURIComponent(f.id)}`) },
                    { label: t("admin:peopleFileRemove"), onSelect: () => viewing && void removeIt(viewing, f), danger: true },
                  ]}
                />
              ))}
            </ul>
          </div>
        )}
      </Drawer>
      {confirmDialog}
    </div>
  );
}
